/**
 * מאזן — reading a PERIODIC report straight from the pension fund / insurer
 * (דוח שנתי / דוח רבעוני לעמית, למבוטח), IN THE BROWSER.
 *
 * The second kind of report a customer may have besides the clearing-house
 * one. Every company lays it out differently, but the regulator fixes the
 * wording of the lines that matter, so it is read by LABEL, not by position:
 * "קצבה חודשית צפויה בפרישה", "יתרת הכספים … בסוף הרבעון", "יתרת החיסכון
 * המצטבר ל־31.12.2024", "תאריך הדוח", the reporting period, and the deposit
 * table. A label's value is the run of numbers just to its LEFT on the same
 * line (RTL) — two report boxes often share a line, so the run stops at the
 * next text. When a row lists components and a total, the total must equal
 * their sum, or the value is left empty rather than guessed.
 *
 * What the report is (pension fund / provident / study fund / policy) comes
 * from its TITLE ("דוח שנתי … בקרן פנסיה חדשה"), never from the company's
 * letterhead, which names every product line it runs.
 *
 * Returns null when the file is not a periodic pension report at all.
 */
import type { Deposit, MaslakaFileResult, MaslakaItem, MaslakaPerson, PdfItem } from './maslaka'

export const NOT_PENSION_REPORT =
  'הקובץ אינו דוח מסלקה פנסיונית ואינו דוח שנתי או רבעוני מקרן פנסיה, קופת גמל, קרן השתלמות או ביטוח מנהלים — לא נקראו ממנו נתונים.'

interface Span { item: PdfItem; start: number; end: number }
interface Line { y: number; items: PdfItem[]; text: string; spans: Span[] }

const norm = (s: unknown): string =>
  String(s || '').replace(/[‎‏‪-‮]/g, '').replace(/[״“”]/g, '"').replace(/[׳‘’]/g, "'").replace(/\s+/g, ' ').trim()

const DATE = String.raw`\d{1,2}[./]\d{1,2}[./]\d{2,4}`
/** "1.1.2024", "31/12/2024", "31.12.24" → 2024-12-31 */
export function periodicDate(s: string): string {
  const m = norm(s).match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/)
  if (!m) return ''
  const y = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10)
  const mo = parseInt(m[2], 10)
  const d = parseInt(m[1], 10)
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1950 || y > 2100) return ''
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** a money cell: "1,311,040.48", "-570", "0" — not a date, not a percent */
const isMoney = (s: string) => /^-?\d{1,3}(?:,\d{3})*(?:\.\d+)?-?$|^-?\d+(?:\.\d+)?-?$/.test(norm(s))
const money = (s: string) => Math.abs(parseFloat(norm(s).replace(/[,-]/g, '')))
/** stray marks between a label and its value */
const isFiller = (s: string) => /^[-–*₪%:.()\s]*$/.test(norm(s))

/** one line = items at the same height (±3pt), read right-to-left */
function toLines(items: PdfItem[]): Line[] {
  const lines: Line[] = []
  let cur: Line | null = null
  for (const it of items.slice().sort((a, b) => b.y - a.y || b.x - a.x)) {
    if (!cur || cur.y - it.y > 3) {
      cur = { y: it.y, items: [], text: '', spans: [] }
      lines.push(cur)
    }
    cur.items.push(it)
  }
  for (const l of lines) {
    l.items.sort((a, b) => b.x - a.x)
    let text = ''
    for (const it of l.items) {
      const s = norm(it.str)
      if (!s) continue
      if (text) text += ' '
      l.spans.push({ item: it, start: text.length, end: text.length + s.length })
      text += s
    }
    l.text = text
  }
  return lines
}

/** the items a text match covers */
const itemsIn = (l: Line, start: number, end: number) => l.spans.filter((s) => s.end > start && s.start < end).map((s) => s.item)

/** the run of numbers just left of x on a line (nearest first); stops at text */
function numbersLeftOf(l: Line, x: number): number[] {
  const out: number[] = []
  for (const it of l.items) {
    if (it.x >= x) continue
    const s = norm(it.str)
    if (isMoney(s)) out.push(money(s))
    else if (isFiller(s)) continue
    else break
  }
  return out
}

/**
 * One value from a run: a single number, or — when the row lists components and
 * a total (the total is the leftmost, last in the run) — the total, but only
 * when it really is the sum of the components. Anything else: null.
 */
function totalOf(run: number[], skipFirst = false): number | null {
  if (!run.length) return null
  if (run.length === 1) return run[0]
  const last = run[run.length - 1]
  const tol = (n: number) => Math.max(0.06, n * 0.0005)
  for (const from of skipFirst ? [1, 0] : [0]) {
    const parts = run.slice(from, -1)
    if (parts.length && Math.abs(parts.reduce((a, b) => a + b, 0) - last) <= tol(last)) return last
  }
  return null
}

interface Hit { line: Line; index: number; match: RegExpExecArray; left: number }
function findAll(lines: Line[], re: RegExp): Hit[] {
  const out: Hit[] = []
  const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g')
  lines.forEach((line, index) => {
    g.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = g.exec(line.text))) {
      const items = itemsIn(line, m.index, m.index + m[0].length)
      if (items.length) out.push({ line, index, match: m, left: Math.min(...items.map((i) => i.x)) })
      if (!m[0].length) g.lastIndex++
    }
  })
  return out
}

/** the text right after a label, up to the next label on the line */
function textAfter(lines: Line[], re: RegExp): string {
  for (const l of lines) {
    const m = l.text.match(re)
    if (!m || m.index == null) continue
    const rest = l.text.slice(m.index + m[0].length).replace(/^[\s:.]+/, '')
    const v = rest.split(/\s(?=(?:מספר|מס'|סוג|סטטוס|שם|תאריך|מעמד|ת\.?ז)(?=\s|:|$))/)[0].trim()
    if (v) return v.slice(0, 60)
  }
  return ''
}

/* ─── what the report is ──────────────────────────────────────────────────── */
type ProductKind = 'pension' | 'hishtalmut' | 'gemelInvest' | 'gemel' | 'policy' | 'risk'

/** title only — the letterhead ("קרנות פנסיה וקופות גמל בע"מ") names every product line */
function classify(title: string, all: string): ProductKind | null {
  const t = title
  if (/ריסק|אובדן כושר|סיעוד|ביטוח בריאות|ביטוח משכנתא/.test(t)) return 'risk'
  if (/השתלמות/.test(t)) return 'hishtalmut'
  if (/גמל להשקעה/.test(t)) return 'gemelInvest'
  if (/קרן (?:ה)?פנסיה|בקרן (?:ה)?פנסיה|קרנות פנסיה חדשות/.test(t)) return 'pension'
  if (/קופת (?:ה)?גמל|בקופת|קופת תגמולים/.test(t)) return 'gemel'
  if (/ביטוח|פוליס|מבוטח/.test(t)) return 'policy'
  // no recognisable title — fall back to the fields only one kind of report has
  if (/סוג הקרן|ריכוז נתונים לעמית בקרן הפנסיה/.test(all)) return 'pension'
  if (/מספר (?:ה)?פוליסה|שם (?:ה)?פוליסה/.test(all)) return 'policy'
  return null
}

const KIND_LABEL: Record<ProductKind, string> = {
  pension: 'קרן פנסיה',
  hishtalmut: 'קרן השתלמות',
  gemelInvest: 'גמל להשקעה',
  gemel: 'קופת גמל',
  policy: 'ביטוח מנהלים',
  risk: 'ביטוח סיכונים',
}

const COMPANIES = [
  'מגדל', 'הראל', 'כלל', 'מנורה', 'הפניקס', 'הכשרה', 'אלטשולר שחם', 'מור', 'ילין לפידות', 'אנליסט', 'מיטב', 'פסגות',
  'הלמן אלדובי', 'איילון', 'אינפיניטי', 'ביטוח ישיר', 'אקסלנס', 'מבטחים', 'מקפת', 'עמיתים', 'סלייס', 'גלובל נט', 'ווב',
]

/* ─── the report ──────────────────────────────────────────────────────────── */
export function parsePeriodicReportPages(pages: PdfItem[][], fileName: string): MaslakaFileResult | null {
  const out: MaslakaFileResult = { file: fileName, person: null, funds: [], capital: [], skipped: [], error: '' }
  const lines = pages.flatMap(toLines)
  if (!lines.length) return null
  const all = lines.map((l) => l.text).join('\n')

  // ── title (page 1): "דוח שנתי מפורט לעמיתים בקרן פנסיה חדשה" ──
  const first = toLines(pages[0] || [])
  const titleIdx = first.findIndex((l) => /דו"?ח\s+(?:שנתי|רבעוני|תקופתי|חצי שנתי)/.test(l.text))
  const title = titleIdx === -1 ? '' : first.slice(titleIdx, titleIdx + 3).map((l) => l.text).join(' ')
  const kind = classify(title, all)

  // ── when: the reporting period, else the report date ──
  const period = all.match(new RegExp(String.raw`(?:מתאריך|לתקופה|מיום|לתקופה שמיום)\s*(${DATE})\s*(?:-|–|עד(?:\s+(?:תאריך|ליום|יום))?)\s*(${DATE})`))
  const reportDate = all.match(new RegExp(String.raw`תאריך (?:ה)?דו"?ח\s*:?\s*(${DATE})`))
  const periodStart = period ? periodicDate(period[1]) : ''
  const asOf = (period && periodicDate(period[2])) || (reportDate && periodicDate(reportDate[1])) || ''

  const hasBalance = /יתרת\s+(?:ה)?(?:כספים|חיסכון|צבירה)/.test(all)
  const hasPension = /קצב(?:ה|ת)[^\d\n]{0,30}צפוי/.test(all)
  // not a periodic pension report at all (a payslip, an invoice, an ID scan…)
  if (!kind || (!title && !hasBalance && !hasPension) || (!hasBalance && !hasPension && kind !== 'risk')) return null

  // ── the saver ──
  const name = (all.match(/שם (?:ה)?(?:עמית|מבוטח(?:\/ת|\/ה)?|חוסך|לקוח)\s*:?\s*([^\n]+?)\s*(?=מספר|מס'|ת\.?ז|תעודת|\n|$)/) || [])[1] || ''
  const id = (all.match(/(?:ת\.?\s?ז|תעודת זהות|מספר זהות|מס' זהות)[.:\s]*(\d{5,9})(?!\d)/) || [])[1] || ''
  const birth = (all.match(new RegExp(String.raw`תאריך לידה\s*:?\s*(${DATE})`)) || [])[1] || ''
  const sex = (all.match(/מין(?: ה?עמית| ה?מבוטח(?:\/ת)?)?\s*:?\s*(זכר|נקבה|ז|נ)(?=\s|\n|$)/) || [])[1] || ''
  const person: MaslakaPerson = {
    name: norm(name).slice(0, 40),
    id: id.replace(/^0+/, ''),
    birth: periodicDate(birth),
    gender: /^ז/.test(sex) ? 'male' : /^נ/.test(sex) ? 'female' : '',
  }
  if (person.name || person.id) out.person = person

  // ── who runs it ──
  const fundName = textAfter(lines, /שם (?:ה)?(?:קרן|קופה|קופת הגמל|קרן ההשתלמות)(?=\s|:)/)
  const byTitle = (title.match(/בחברת\s+(.+?)(?:\s+חברה לביטוח|\s+בע"מ|\s+לביטוח|$)/) || [])[1] || ''
  const company = norm(fundName || byTitle || COMPANIES.find((c) => all.includes(c)) || '')
  const planName = textAfter(lines, /שם (?:ה)?(?:פוליסה|תכנית|תוכנית)(?=\s|:)/)
  const policy = (all.match(/מספר (?:ה)?(?:פוליסה|חשבון|תיק|עמית בקופה)\s*:?\s*(\d{4,})/) || [])[1] || ''
  const reportKind = /רבעוני/.test(title) ? 'דוח רבעוני' : /שנתי/.test(title) ? 'דוח שנתי' : 'דוח תקופתי'

  // A pension annual report may hold two funds (מקיפה + כללית) one after the
  // other — each starts with its own "סוג הקרן" line.
  const starts = lines.map((l, k) => (/^סוג (?:ה)?קרן(?=\s|:|$)/.test(l.text) ? k : -1)).filter((k) => k !== -1)
  const segments = kind === 'pension' && starts.length > 1
    ? starts.map((s, k) => lines.slice(k === 0 ? 0 : Math.max(0, s - 2), k + 1 < starts.length ? Math.max(0, starts[k + 1] - 2) : lines.length))
    : [lines]

  segments.forEach((seg, seq) => {
    const segText = seg.map((l) => l.text).join('\n')

    // monthly pension at retirement, on today's balance (never the "with further deposits" one)
    let kitzba: number | null = null
    let gilPrisha = 0
    for (const h of findAll(seg, /(?:ה)?קצב(?:ה|ת)(?: זקנה)?[^\d]{0,30}צפוי[^\d]{0,50}/)) {
      if (/בהנחת המשך|כולל המשך|עם המשך|בהמשך הפקדות/.test(h.line.text)) continue
      let left = h.left
      // "…בפרישה בגיל 67 **" — the age belongs to the label, not the value
      if (/בגיל\s*$/.test(h.match[0])) {
        const rest = h.line.text.slice(h.match.index + h.match[0].length)
        const age = rest.match(/^(\d{2})\s*\**/)
        if (age) {
          gilPrisha = parseInt(age[1], 10)
          const its = itemsIn(h.line, h.match.index, h.match.index + h.match[0].length + age[0].length)
          left = Math.min(...its.map((i) => i.x))
        }
      }
      const v = totalOf(numbersLeftOf(h.line, left))
      if (v != null) {
        kitzba = v
        break
      }
    }

    // balance at the END of the period (not the opening one)
    let balance: number | null = null
    let balanceDate = ''
    for (const h of findAll(seg, /יתרת\s+(?:ה)?(?:כספים|חיסכון|צבירה)(?:\s+(?:ה)?(?:מצטבר|צבור|הצבור|המצטבר))?(?:\s+(?:בפוליסה|בקרן|בקופה|בחשבון|לקצבה))?\s*(?:(?:ב|ל)סוף(?:\s+(?:ה)?(?:רבעון|שנה|תקופה|תקופת הדיווח))?|(?:מעודכנת\s+)?ל-?\s*(\d{1,2}[./]\d{1,2}[./]\d{2,4})|(\d{1,2}[./]\d{1,2}[./]\d{2,4}))/)) {
      if (/בתחילת|מעודכנת/.test(h.match[0])) continue
      const when = h.match[1] || h.match[2] ? periodicDate(h.match[1] || h.match[2]) : asOf
      const v = totalOf(numbersLeftOf(h.line, h.left))
      if (v == null) continue
      // prefer the line dated at the report date; else the latest one
      if (balance == null || (balanceDate !== asOf && (when === asOf || when > balanceDate))) {
        balance = v
        balanceDate = when
      }
    }

    const opened = periodicDate((segText.match(new RegExp(String.raw`(?:מועד|תאריך)\s+(?:ה)?(?:הצטרפות|פתיחת? (?:ה)?(?:תכנית|חשבון|פוליסה)?|תחילת (?:ה)?(?:ביטוח|פוליסה))[^\d\n]{0,25}?(${DATE})`)) || [])[1] || '')
    const status = (segText.match(/(?:סטטוס(?: ה)?(?:פוליסה|חשבון|עמית)?|סוג עמית|מעמד (?:ה)?(?:פוליסה|חשבון))\s*:?\s*(לא פעיל|פעיל|מוקפא\S*|מסולק\S*|מבוטל\S*)/) || [])[1] || ''
    const deposits = readDeposits(seg, asOf)

    const segKind = kind === 'pension' ? (segText.match(/סוג (?:ה)?קרן\s*:?\s*(\S+)/) || [])[1] || (title.match(/מקיפה|כללית|משלימה|ותיקה/) || [])[0] || '' : ''
    const common: MaslakaItem = {
      body: company,
      plan: '',
      acct: (policy || (kind === 'pension' ? person.id : '')).replace(/^0+/, ''),
      opened,
      balance: balance ?? 0,
      asOf: balanceDate || asOf,
      active: /^פעיל/.test(status),
      kind: '',
      deposits: deposits.rows,
      depositsFrom: deposits.complete ? periodStart : undefined,
      source: 'insurer',
      reportKind,
      dk: `ins:${kind}`,
      seq,
    }

    if (kind === 'pension') {
      if (/ותיק/.test(segKind)) {
        // old pension fund — פסיקתא on the balance (as the clearing-house parser)
        out.capital.push({ ...common, plan: [company, 'קרן פנסיה ותיקה'].filter(Boolean).join(' — '), kind: 'קרן פנסיה ותיקה', pesikta: true })
      } else if (/מקיפה|כללית|משלימה/.test(segKind)) {
        const mashlima = /כללית|משלימה/.test(segKind)
        out.funds.push({
          ...common,
          plan: [company, mashlima ? 'משלימה' : 'מקיפה'].filter(Boolean).join(' '),
          kind: mashlima ? 'קרן פנסיה משלימה' : 'קרן פנסיה מקיפה',
          dk: `ins:${mashlima ? 'mashlima' : 'makifa'}`,
          kitzba: kitzba ?? 0,
          gilPrisha,
        })
      } else {
        out.skipped.push({ ...common, plan: [company, 'קרן פנסיה'].filter(Boolean).join(' — '), kind: 'קרן פנסיה — סוג לא מזוהה', why: 'לא זוהה בדוח אם הקרן מקיפה או כללית — יש להזין אותה ידנית' })
      }
    } else if (kind === 'risk' || (kind === 'policy' && !(balance && balance > 0))) {
      out.skipped.push({ ...common, plan: [company, planName || 'ביטוח סיכונים'].filter(Boolean).join(' — '), kind: 'ביטוח סיכונים', why: 'פוליסת ביטוח ללא חיסכון — אינה נכס לאיזון' })
    } else if (kind === 'policy' && (kitzba ?? 0) > 0) {
      // managers' insurance with an expected pension — an annuity product
      out.funds.push({
        ...common,
        plan: [company, 'ביטוח מנהלים'].filter(Boolean).join(' — ') + (planName ? ` (${planName})` : ''),
        kind: 'ביטוח מנהלים',
        dk: 'ins:policy',
        kitzba: kitzba ?? 0,
        gilPrisha,
      })
    } else {
      const label = KIND_LABEL[kind]
      out.capital.push({
        ...common,
        plan: [company, label].filter(Boolean).join(' — ') + (planName ? ` (${planName})` : ''),
        kind: label,
        kitzba: kitzba ?? undefined,
        hishtalmut: kind === 'hishtalmut',
        pesikta: kind === 'gemel',
      })
    }
  })

  const item = [...out.funds, ...out.capital][0]
  const at = asOf ? asOf.split('-').reverse().join('/') : ''
  out.notes = [
    `זוהה ${reportKind}${company ? ` מ${company}` : ''}: ${kind === 'pension' ? out.funds[0]?.kind || 'קרן פנסיה' : KIND_LABEL[kind]}${at ? `, נכון ל־${at}` : ''}.` +
      (item && !item.balance ? ' היתרה לא נקראה מהדוח — יש להשלים ידנית.' : '') +
      (out.funds.some((f) => !f.kitzba) ? ' הקצבה החודשית לא נקראה מהדוח — יש להשלים ידנית.' : ''),
  ]
  if (!asOf) out.notes.push('לא נמצא בדוח תאריך הדוח — לא ניתן לבדוק אותו מול מועד הקרע.')
  return out
}

/**
 * The deposit table ("פירוט הפקדות"): rows with a value date and usually a salary
 * month, then salary + components + total. Only deposits that are IN the
 * reported balance count — "deposited after the end of the year" rows (value
 * date after the report date) are not. `complete` = the table was read in full
 * (or says there were none), so it can be trusted from the period start.
 */
function readDeposits(seg: Line[], asOf: string): { rows: Deposit[]; complete: boolean } {
  const rows: Deposit[] = []
  let inTable = false
  let found = false
  let none = false
  let unreadable = false
  for (const l of seg) {
    if (/פירוט (?:ה)?הפקדות|הפקדות (?:ל|ב)(?:קרן|קופה|פוליסה|חשבון) בתקופת/.test(l.text)) {
      inTable = true
      found = true
      continue
    }
    if (!inTable) continue
    // the next numbered section ("א . 5 . תנועות…", "ו. פרטי הסוכן") ends it
    if (/^[א-ת]\s*\.\s*(?:\d+\s*\.\s*)?[א-ת]/.test(l.text) || /^חלק [א-ת]'/.test(l.text)) {
      inTable = false
      continue
    }
    if (/לא היו הפקדות|לא בוצעו הפקדות|אין הפקדות/.test(l.text)) {
      none = true
      continue
    }
    const dateItem = l.items.find((it) => /^\d{1,2}[./]\d{1,2}[./]\d{4}$/.test(norm(it.str)))
    if (!dateItem) continue
    // late-payment interest is credited as returns, not a deposit — the report's
    // own deposit total leaves it out, and so do we
    if (/^ריבית/.test(l.text)) continue
    const monthItem = l.items.find((it) => /^\d{1,2}[./]\d{4}$/.test(norm(it.str)))
    const amount = totalOf(numbersLeftOf(l, (monthItem || dateItem).x), !!monthItem)
    if (amount == null) {
      unreadable = true
      continue
    }
    const valueDate = periodicDate(dateItem.str)
    if (amount > 0 && valueDate && (!asOf || valueDate <= asOf))
      rows.push({ valueDate, salaryMonth: monthItem ? norm(monthItem.str) : '', amount: Math.round(amount * 100) / 100, source: 'INSURER' })
  }
  return { rows, complete: found && !unreadable && (rows.length > 0 || none) }
}
